import React, { LegacyRef } from "react";
import classNames from "classnames";
import styles from "./inputV2.module.scss";
import HelpIcon from "@icons/inputHelpIcon.svg";
import { Tooltip } from "../../tooltip/tooltip";

export const InputV2 = (props: InputV2Props) => {
  const {
    label,
    prependIcon,
    prependNode,
    appendNode,
    appendButton,
    helpNode,
    helpIcon,
    hintClassName,
    hintMessage,
    errorMessage,
    errorClassName,
    wrapperClassName,
    appendNodeClassName,
    prependNodeClassName,
    prependIconClassName,
    inputNodeClassName,
    id,
    inputNode,
    hasError,
    sizeVariant,
    inputRef,
    ...rest
  } = props;

  const printLabelRow = () => {
    if (!props.label) {
      return null;
    }

    return (
      <div className={classNames(styles["labelWrapper"])}>
        {props.label && (
          <label
            className={classNames(props.labelClassName, styles["label"])}
            htmlFor={props.id}
          >
            {props.label}
          </label>
        )}
      </div>
    );
  };

  const printPrependNodes = () => {
    if (props.prependNode) {
      return (
        <div
          data-testid="prependNodeContainer"
          className={classNames(
            prependNodeClassName,
            styles["prependNodeContainer"],
          )}
        >
          {props.prependNode}
        </div>
      );
    }

    return null;
  };

  const PrintPrependIcon = () => {
    if (props.prependIcon) {
      return (
        <div
          data-testid="prependIconContainer"
          className={classNames(prependIconClassName, {
            [styles["prependsmIconContainer"]]: props.sizeVariant !== "md",
            [styles["prependmdIconContainer"]]: props.sizeVariant === "md",
          })}
        >
          {props.prependIcon}
        </div>
      );
    }
    return null;
  };

  const printAppendNodes = () => {
    if (props.appendNode) {
      return (
        <div
          data-testid="appendNodeContainer"
          className={classNames(
            props.appendNodeClassName,
            styles["appendNodeContainer"],
          )}
        >
          {appendNode}
        </div>
      );
    } else if (props.appendButton) {
      return props.appendButton;
    }

    return null;
  };

  const PrintAppendIcon = () => {
    if (props.helpNode) {
      return (
        <div
          data-testid="appendIconContainer"
          className={classNames(styles["appendIconContainer"])}
        >
          <Tooltip
            triggerNode={props.helpIcon || <HelpIcon />}
            contentNode={props.helpNode}
          />
        </div>
      );
    }
    return null;
  };

  const printInputNode = () => {
    if (props.inputNode) {
      return (
        <div
          className={classNames(styles["inputWrapper"], {
            [styles["hasPrependNode"]]: !!props.prependNode,
            [styles["hasAppendNode"]]:
              !!props.appendNode || !!props.appendButton,
          })}
        >
          <PrintPrependIcon />
          {React.cloneElement(props.inputNode as any, {
            className: classNames(
              {
                [styles["input"]]: props.sizeVariant !== "md",
                [styles["inputBig"]]: props.sizeVariant === "md",
              },
              props.inputNodeClassName,
              props.hasError && props.errorMessage && styles["error"],
            ),
          })}
          <PrintAppendIcon />
        </div>
      );
    }

    return (
      <div
        className={classNames(styles["inputWrapper"], {
          [styles["hasPrependNode"]]: !!props.prependNode,
          [styles["hasAppendNode"]]: !!props.appendNode || !!props.appendButton,
        })}
      >
        <PrintPrependIcon />
        <input
          className={classNames(
            {
              [styles["input"]]: props.sizeVariant !== "md",
              [styles["inputBig"]]: props.sizeVariant === "md",
            },
            props.inputNodeClassName,
            props.hasError && props.errorMessage && styles["error"],
          )}
          ref={inputRef}
          {...rest}
        />
        <PrintAppendIcon />
      </div>
    );
  };

  const printHintRow = () => {
    if (!props.hintMessage && !props.errorMessage) {
      return undefined;
    }

    if (props.hasError && props.errorMessage) {
      return (
        <p className={classNames(props.errorClassName, styles["errorMessage"])}>
          {props.errorMessage}
        </p>
      );
    }

    return (
      <p className={classNames(props.hintClassName, styles["hintMessage"])}>
        {props.hintMessage}
      </p>
    );
  };

  return (
    <div className={classNames(styles["wrapper"], props.wrapperClassName)}>
      {printLabelRow()}
      <div
        className={classNames(
          props.inputContainerClassName,
          styles["inputContainer"],
        )}
      >
        {printPrependNodes()}
        {printInputNode()}
        {printAppendNodes()}
      </div>
      {printHintRow()}
    </div>
  );
};

export interface InputV2Props extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  prependNode?: React.ReactNode;
  prependIcon?: React.ReactNode;
  appendNode?: React.ReactNode;
  inputNode?: React.ReactNode;
  appendButton?: React.ReactNode;
  hintMessage?: string;
  hintClassName?: string;
  errorMessage?: string;
  errorClassName?: string;
  helpNode?: React.ReactNode;
  helpIcon?: React.ReactNode;
  wrapperClassName?: string;
  labelClassName?: string;
  inputContainerClassName?: string;
  prependNodeClassName?: string;
  prependIconClassName?: string;
  inputNodeClassName?: string;
  appendNodeClassName?: string;
  hasError?: boolean;
  sizeVariant?: "sm" | "md";
  inputRef?: LegacyRef<any>;
}
